import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Mic, BrainCircuit, ListChecks, ShieldCheck, Check, Sparkles } from "lucide-react";

/**
 * Animierter 4-Phasen Demo-Loop für die Pflege-KI Assistenz (Interview-Modus).
 * Loopt automatisch im Espresso-Demo-Bereich von HeldenCampus.jsx.
 *
 * Phasen:
 *   0 · Chip-Quickstart   (12 Kernfragen-Kategorien, eine wird aktiv)
 *   1 · Sprach-Diktat     (Wave + Live-Transcript Typewriter)
 *   2 · KI strukturiert   (SIS A1–A6 fade-in)
 *   3 · NBA + Pflegegrad-Tendenz (Module-Chips + Ampel)
 */

const KATEGORIE_CHIPS = [
  "Mobilität", "Kognition", "Verhalten", "Selbstversorgung",
  "Ernährung", "Ausscheidung", "Medikamente", "Verordnungen",
  "Risiken", "Alltag", "Veränderung", "Soziales",
];

const ACTIVE_CHIP_INDEX = 0; // „Mobilität" wird in Phase 0 ausgewählt

const RAW_TRANSCRIPT =
  "Frau M. geht mit Rollator, letzter Sturz vor vier Wochen, abends sehr unsicher.";

const SIS_LINES = [
  { label: "A1 · Kognition", value: "Orientiert, geringfügige Vergesslichkeit." },
  { label: "A2 · Mobilität", value: "Rollator-mobil, Sturzrisiko erhöht." },
  { label: "A3 · Selbstversorgung", value: "Bei Körperpflege punktuell Hilfebedarf." },
  { label: "A4 · Krankheit", value: "Z. n. Sturz, NRS 4/10 (Hüfte rechts)." },
  { label: "A5 · Alltag", value: "Tagesstruktur stabil, soziale Kontakte rege." },
  { label: "A6 · Außerhäuslich", value: "Begleitung beim Spaziergang gewünscht." },
];

const NBA_MODULE = ["Mobilität", "Selbstversorgung", "Krankheit"];
const NBA_RISIKEN = ["Sturz", "Schmerz"];

const PHASE_DURATION_MS = {
  0: 3000, // chip quickstart
  1: 4200, // voice + transcript
  2: 4400, // SIS reveal
  3: 3200, // NBA tendenz
};

export default function PflegeKiDemoLoop() {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setPhase((p) => (p + 1) % 4), PHASE_DURATION_MS[phase]);
    return () => clearTimeout(t);
  }, [phase]);

  return (
    <div className="rounded-3xl bg-espresso border border-espresso-line p-6 relative overflow-hidden"
      data-testid="pki-demo-loop">

      {/* Phase-Header */}
      <div className="flex items-center justify-between mb-5 min-h-[20px]">
        <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-rose">
          <PhaseIcon phase={phase} />
          <AnimatePresence mode="wait">
            <motion.span
              key={phase}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.25 }}
            >
              {PHASE_LABELS[phase]}
            </motion.span>
          </AnimatePresence>
        </div>
        <PhaseDots phase={phase} />
      </div>

      {/* Phase-Content */}
      <div className="relative min-h-[330px]">
        <AnimatePresence mode="wait">
          {phase === 0 && <ChipQuickstartPhase key="chips" />}
          {phase === 1 && <DiktatPhase key="dik" />}
          {phase === 2 && <SisPhase key="sis" />}
          {phase === 3 && <NbaPhase key="nba" />}
        </AnimatePresence>
      </div>

      <p className="mt-5 text-[11.5px] text-cream-100/55 text-center leading-snug">
        Live-Loop · KI-Interview · Whisper-Diktat · Claude Sonnet 4.5 · NBA-Auswertung
      </p>
    </div>
  );
}

const PHASE_LABELS = {
  0: "Chip-Quickstart · 12 Kernfragen",
  1: "Sprach-Diktat · Whisper",
  2: "SIS · Claude Sonnet 4.5",
  3: "NBA + Pflegegrad-Tendenz",
};

function PhaseIcon({ phase }) {
  const Icon = [Sparkles, Mic, BrainCircuit, ShieldCheck][phase];
  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={phase}
        initial={{ rotate: -90, opacity: 0 }}
        animate={{ rotate: 0, opacity: 1 }}
        exit={{ rotate: 90, opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="inline-flex"
      >
        <Icon className="h-3.5 w-3.5" strokeWidth={2} />
      </motion.span>
    </AnimatePresence>
  );
}

function PhaseDots({ phase }) {
  return (
    <div className="flex items-center gap-1.5" aria-hidden>
      {[0, 1, 2, 3].map((i) => (
        <span
          key={i}
          className={`h-1 rounded-full transition-all duration-500 ${
            i === phase
              ? "w-6 bg-brand-rose"
              : i < phase
              ? "w-2.5 bg-brand-rose/40"
              : "w-2.5 bg-cream-50/15"
          }`}
        />
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
 *  Phase 0 · Chip-Quickstart
 *  Die 12 Interview-Kategorien tauchen gestaffelt auf,
 *  eine wird am Ende „angetippt" (Active-Highlight).
 * ────────────────────────────────────────────────────────────*/
function ChipQuickstartPhase() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="absolute inset-0 flex flex-col"
    >
      <div className="inline-flex items-center gap-2 rounded-full bg-brand-rose/15 text-brand-rose px-4 h-9 text-[12px] font-semibold uppercase tracking-[0.18em] self-start mb-4">
        <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
        Tippe einen Bereich an · Start in 1 Sek.
      </div>

      <div className="flex flex-wrap gap-2 max-w-md">
        {KATEGORIE_CHIPS.map((chip, i) => {
          const isActive = i === ACTIVE_CHIP_INDEX;
          return (
            <motion.span
              key={chip}
              initial={{ opacity: 0, y: 6 }}
              animate={{
                opacity: 1,
                y: 0,
                scale: isActive ? [1, 1.08, 1] : 1,
              }}
              transition={{
                duration: 0.35,
                delay: i * 0.06,
                scale: { delay: 1.4, duration: 0.6 },
              }}
              className={`inline-flex items-center px-3 h-8 rounded-full text-[12px] font-semibold border ${
                isActive
                  ? "bg-brand-rose text-espresso border-brand-rose shadow-[0_0_0_4px_rgba(231,207,162,0.18)]"
                  : "bg-cream-50/8 text-cream-50 border-cream-50/18"
              }`}
            >
              {isActive && <Check className="h-3 w-3 mr-1" strokeWidth={2.4} />}
              {chip}
            </motion.span>
          );
        })}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 1.6 }}
        className="mt-6 rounded-2xl bg-espresso-card border border-espresso-line p-4 max-w-md"
      >
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-rose mb-1.5">
          Frage 1 von 12 · Mobilität
        </div>
        <div className="text-[13.5px] text-cream-50 leading-snug">
          Wie bewegt sich die Person im Alltag?
        </div>
        <div className="mt-1 text-[11.5px] text-cream-100/55 italic">
          z. B. selbstständig, Rollator, Rollstuhl, bettlägerig
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ────────────────────────────────────────────────────────────
 *  Phase 1 · Sprach-Diktat (Wave + Live-Transcript)
 * ────────────────────────────────────────────────────────────*/
function DiktatPhase() {
  const [shown, setShown] = useState("");
  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setShown(RAW_TRANSCRIPT.slice(0, i));
      if (i >= RAW_TRANSCRIPT.length) clearInterval(id);
    }, 28);
    return () => clearInterval(id);
  }, []);

  const heights = [
    20, 34, 26, 48, 62, 50, 40, 72, 54, 38,
    64, 30, 44, 78, 56, 32, 50, 66, 40, 24,
    54, 74, 46, 32, 58, 28, 44, 38,
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="absolute inset-0 flex flex-col"
    >
      <div className="inline-flex items-center gap-2 rounded-full bg-brand-rose/15 text-brand-rose px-4 h-9 text-[12px] font-semibold uppercase tracking-[0.18em] self-start mb-4">
        <span className="inline-block w-2 h-2 rounded-full bg-brand-rose animate-pulse" />
        Diktat · Hände frei am Patientenbett
      </div>

      {/* Wave */}
      <div className="flex items-center justify-center gap-[3px] h-[56px] mb-4">
        {heights.map((h, i) => (
          <motion.span
            key={i}
            className="w-[3.5px] rounded-full bg-brand-rose"
            animate={{
              height: [`${h * 0.45}%`, `${h}%`, `${h * 0.55}%`, `${h * 0.85}%`, `${h * 0.4}%`],
            }}
            transition={{
              duration: 0.9 + (i % 5) * 0.1,
              repeat: Infinity,
              repeatType: "reverse",
              delay: i * 0.025,
              ease: "easeInOut",
            }}
            style={{ height: `${h}%` }}
          />
        ))}
      </div>

      <div className="rounded-2xl bg-espresso-card border border-espresso-line p-4 text-[13.5px] text-cream-100/90 leading-[1.65] font-mono flex-1">
        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-brand-rose mb-1.5">
          Whisper · Live-Transkript
        </div>
        {shown}
        <motion.span
          className="inline-block w-[6px] h-[14px] bg-brand-rose ml-0.5 -mb-0.5"
          animate={{ opacity: [1, 0, 1] }}
          transition={{ duration: 0.6, repeat: Infinity }}
        />
      </div>

      <div className="mt-3 flex items-center justify-between text-[10.5px] uppercase tracking-[0.22em] font-bold text-cream-100/45">
        <span className="font-mono tabular-nums">00:12 · Audio</span>
        <span className="text-brand-rose">→ KI strukturiert SIS</span>
      </div>
    </motion.div>
  );
}

/* ────────────────────────────────────────────────────────────
 *  Phase 2 · SIS A1–A6 (stagger reveal)
 * ────────────────────────────────────────────────────────────*/
function SisPhase() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="absolute inset-0 flex flex-col"
    >
      <div className="inline-flex items-center gap-2 rounded-full bg-brand-rose/15 text-brand-rose px-4 h-9 text-[12px] font-semibold uppercase tracking-[0.18em] self-start mb-4">
        <BrainCircuit className="h-3.5 w-3.5" strokeWidth={2} />
        Claude Sonnet 4.5 · 4.1 Sek.
      </div>

      <div className="rounded-2xl bg-espresso-card border border-espresso-line p-5 flex-1">
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-cream-50 mb-3">
          SIS-Entwurf · editierbar · A1–A6
        </div>
        <div className="space-y-2 text-[12.5px] text-cream-100/90 leading-snug">
          {SIS_LINES.map((line, i) => (
            <motion.div
              key={line.label}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.15 + i * 0.28 }}
            >
              <span className="text-brand-rose font-semibold">{line.label}:</span>{" "}
              {line.value}
            </motion.div>
          ))}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 2.0 }}
        className="mt-3 flex items-center gap-2 text-[11.5px] text-brand-rose font-semibold"
      >
        <ListChecks className="h-3.5 w-3.5" strokeWidth={2.6} />
        + Maßnahmenplan generiert
      </motion.div>
    </motion.div>
  );
}

/* ────────────────────────────────────────────────────────────
 *  Phase 3 · NBA-Module + Pflegegrad-Tendenz
 * ────────────────────────────────────────────────────────────*/
function NbaPhase() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="absolute inset-0 flex flex-col items-center justify-center text-center"
    >
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.45, type: "spring", stiffness: 220, damping: 18 }}
        className="inline-flex items-center gap-2 px-5 h-11 rounded-full border-2 border-brand-rose bg-brand-rose/10 text-brand-rose text-[14px] font-bold uppercase tracking-[0.14em]"
      >
        <ShieldCheck className="h-4 w-4" strokeWidth={2.4} />
        Tendenz Pflegegrad 2–3
      </motion.div>
      <p className="mt-2 text-[11px] text-cream-100/55 italic max-w-xs leading-snug">
        Keine offizielle Einstufung — der MD entscheidet abschließend.
      </p>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.5 }}
        className="mt-6 w-full max-w-sm"
      >
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-cream-50 mb-2">
          Erkannte NBA-Module
        </div>
        <div className="flex flex-wrap gap-2 justify-center">
          {NBA_MODULE.map((m, i) => (
            <motion.span
              key={m}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.7 + i * 0.15 }}
              className="inline-flex items-center px-3 h-7 rounded-full bg-brand-rose/20 text-brand-rose text-[11.5px] font-semibold border border-brand-rose/30"
            >
              {m}
            </motion.span>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 1.3 }}
        className="mt-4 w-full max-w-sm"
      >
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-saffron mb-2">
          Risiken
        </div>
        <div className="flex flex-wrap gap-2 justify-center">
          {NBA_RISIKEN.map((r, i) => (
            <motion.span
              key={r}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 1.5 + i * 0.15 }}
              className="inline-flex items-center px-3 h-7 rounded-full bg-saffron/20 text-saffron text-[11.5px] font-semibold border border-saffron/30"
            >
              {r}
            </motion.span>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 2.0 }}
        className="mt-5 text-[11.5px] text-cream-100/55 leading-snug max-w-xs"
      >
        Interview-Zeit:{" "}
        <span className="text-cream-50 font-semibold tabular-nums">6 Min.</span> ·{" "}
        manuell wären es 30–45 Min.
      </motion.div>
    </motion.div>
  );
}
